import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
import { Shield, X, Trophy, Gift, Glasses, Star, Heart, ChevronLeft, ChevronRight, Camera, Sparkles, Play, Diamond } from 'lucide-react';
import { ProfilePanel } from './components/ProfilePanel';
import { FanclubSection } from './components/FanclubSection';
import { MiniGame } from './components/MiniGame';
import { Dashboard } from './components/Dashboard';
import { TokenWallet } from './components/TokenWallet';
import { CelebrityAI } from './components/CelebrityAI';
import { LiveCam } from './components/LiveCam';
import { ChatSection } from './components/ChatSection';
import { GroupChatSection } from './components/GroupChatSection';
import { GamesSection } from './components/GamesSection';
import { StarsSection } from './components/StarsSection';
import { StatsSection } from './components/StatsSection';
import { SocialLifeSection } from './components/SocialLifeSection';
import { InteractiveToySync } from './components/InteractiveToySync';
import { YogaPost } from './components/YogaPost';
import { VRARSection } from './components/VRARSection';
import { AdvancedTokenSystem } from './components/AdvancedTokenSystem';
import { CreatorMarketplace } from './components/CreatorMarketplace';
import { AgeVerification } from './components/AgeVerification';
import { ProfileSettingsButton } from './components/ProfileSettingsButton';
import { NotificationButton } from './components/NotificationButton';
import { AchievementsSection } from './components/AchievementsSection';
import { RewardsSection } from './components/RewardsSection';
import { PremiumContent } from './components/PremiumContent';
import { MembershipSection } from './components/MembershipSection';
import { FloatingActionButton } from './components/FloatingActionButton';
import { AlertSystem } from './components/AlertSystem';
import { RelationshipTracking } from './components/RelationshipTracking';
import { LeftNavBar } from './components/LeftNavBar';
import { ProfileToggle } from './components/ProfileToggle';
import { ChatToggle } from './components/ChatToggle';
import { AngelProfile } from './components/AngelProfile';
import { CustomerProfile } from './components/CustomerProfile';
import { FilteredDiscoveryFeed } from './components/FilteredDiscoveryFeed';
import { TwitterStyleProfile } from './components/TwitterStyleProfile';
import { AngelSocialFeed } from './components/AngelSocialFeed';
import { HarperAngelFeed } from './components/HarperAngelFeed';
import { ZoeyAngelFeed } from './components/ZoeyAngelFeed';
import { SophiaAngelFeed } from './components/SophiaAngelFeed';
import { MiaKhalifaAngelFeed } from './components/MiaKhalifaAngelFeed';
import { KimHollandAngelFeed } from './components/KimHollandAngelFeed';
import { BonnyBlueAngelFeed } from './components/BonnyBlueAngelFeed';
import { BellaBlueAngelFeed } from './components/BellaBlueAngelFeed';
import { CreatorDashboard } from './components/CreatorDashboard';
import { TopHeader } from './components/TopHeader';
import { ThemeProvider } from './components/ThemeContext';
import LanaAngelFeed from './components/LanaAngelFeed';
import { CreatorStudioDashboard } from './components/creator-studio/CreatorStudioDashboard';
import { TranslationProvider } from './components/translation/TranslationContext';
import { AllAngelsGrid } from './components/AllAngelsGrid';
import { UnleashFeature } from './components/unleash/UnleashFeature';
import { AngelData } from './components/unleash/UnleashWizard';
import { QuestionnaireProvider } from './components/personalization/QuestionnaireContext';
import { PhotoRatingFlow } from './components/PhotoRatingFlow';
import { AngelMatchGame } from './components/AngelMatchGame';
import { FileText } from 'lucide-react';
import { SocialPollsSection } from './components/SocialPollsSection';
import { HotOrNotSection } from './components/HotOrNotSection';
import { ProfileSettings } from './components/settings/ProfileSettings';
import { AngelSkinsShop } from './components/AngelSkinsShop';
import { WelcomeVideoModal } from './components/WelcomeVideoModal';
import { BookmarksSection } from './components/BookmarksSection';
import { FavoritesSection } from './components/FavoritesSection';
import { RitualProvider } from './components/rituals/RitualContext';
import { RitualTracker } from './components/rituals/RitualTracker';
import { RitualBoard } from './components/rituals/RitualBoard';
import { RitualRewards } from './components/rituals/RitualRewards';
import { RitualAchievements } from './components/rituals/RitualAchievements';
import { Angel } from './components/selected-angel';
import { RewardHub } from './rewards/RewardHub';
import { ReferralModal } from './components/referrals/ReferralModal';
import { EngagementRituals } from './components/monetization/EngagementRituals';
import { GuidedHelpOverlay } from './components/GuidedHelpOverlay';
// Lazy load the SplashScreen component
const SplashScreen = lazy(() => import('./components/splash/SplashScreen'));
// Import DeepShift and FaceShift modals
import { DeepShiftModal } from './components/ai-shift/DeepShiftModal';
import { FaceShiftModal } from './components/ai-shift/FaceShiftModal';
export function App() {
  // Splash screen state
  const [showSplash, setShowSplash] = useState(true);
  const [splashDone, setSplashDone] = useState(false);
  const [activeSection, setActiveSection] = useState('social_feed');
  const [showVerification, setShowVerification] = useState(false);
  const [hasSeenVerification, setHasSeenVerification] = useState(() => {
    return localStorage.getItem('hasSeenVerification') === 'true';
  });
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);
  const [activeProfile, setActiveProfile] = useState<'angel' | 'customer'>('customer');
  const [activeChat, setActiveChat] = useState<'chat' | 'group_chat'>('chat');
  const [selectedAngel, setSelectedAngel] = useState<Angel | null>(null);
  const [showAngelFeed, setShowAngelFeed] = useState(false);
  const [selectedAngelId, setSelectedAngelId] = useState<string | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [createdAngels, setCreatedAngels] = useState<AngelData[]>(() => {
    const savedAngels = localStorage.getItem('createdAngels');
    return savedAngels ? JSON.parse(savedAngels) : [];
  });
  const [showPhotoRatingFlow, setShowPhotoRatingFlow] = useState(false);
  const [showDailyTargetsModal, setShowDailyTargetsModal] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralRitualId, setReferralRitualId] = useState<string | undefined>(undefined);
  // Add state for DeepShift and FaceShift modals
  const [showDeepShiftModal, setShowDeepShiftModal] = useState(false);
  const [showFaceShiftModal, setShowFaceShiftModal] = useState(false);
  const [currentRitualId, setCurrentRitualId] = useState<string | null>(null);
  // Add state for GuidedHelpOverlay
  const [showGuidedHelp, setShowGuidedHelp] = useState(false);
  // Initialize splash screen logic - moved to top of effects for priority
  useEffect(() => {
    // Check if we should show the splash screen
    const checkSplashStatus = () => {
      try {
        const lastSeen = localStorage.getItem('sinshift_splash_last_seen');
        // Only skip if seen in the last 30 minutes (changed from 24 hours)
        if (lastSeen && Date.now() - Number(lastSeen) < 1800000) {
          setShowSplash(false);
          setSplashDone(true);
          return;
        }
      } catch (e) {
        // Ignore storage errors, default to showing splash
        console.log('Storage error, showing splash', e);
      }
      // Show splash by default
      setShowSplash(true);
    };
    checkSplashStatus();
  }, []);
  // Add effect to show guided help for new users
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    if (!hasSeenTutorial && splashDone) {
      setShowGuidedHelp(true);
      localStorage.setItem('hasSeenTutorial', 'true');
    }
  }, [splashDone]);
  // Add event listeners for DeepShift and FaceShift modals
  useEffect(() => {
    const handleOpenDeepShiftModal = (event: any) => {
      if (event.detail && event.detail.ritualId) {
        setCurrentRitualId(event.detail.ritualId);
      }
      setShowDeepShiftModal(true);
    };
    const handleOpenFaceShiftModal = (event: any) => {
      if (event.detail && event.detail.ritualId) {
        setCurrentRitualId(event.detail.ritualId);
      }
      setShowFaceShiftModal(true);
    };
    document.addEventListener('openDeepShiftModal', handleOpenDeepShiftModal);
    document.addEventListener('openFaceShiftModal', handleOpenFaceShiftModal);
    return () => {
      document.removeEventListener('openDeepShiftModal', handleOpenDeepShiftModal);
      document.removeEventListener('openFaceShiftModal', handleOpenFaceShiftModal);
    };
  }, []);
  // Add event listener for showing guided help
  useEffect(() => {
    const handleShowGuidedHelp = () => {
      setShowGuidedHelp(true);
    };
    document.addEventListener('showGuidedHelp', handleShowGuidedHelp);
    return () => {
      document.removeEventListener('showGuidedHelp', handleShowGuidedHelp);
    };
  }, []);
  // Handle completion of AI creation
  const handleDeepShiftComplete = (result: any) => {
    setShowDeepShiftModal(false);
    // Dispatch AI creation complete event
    const event = new CustomEvent('aiCreationComplete', {
      detail: {
        type: 'deep_shift',
        result,
        ritualId: currentRitualId
      }
    });
    document.dispatchEvent(event);
  };
  const handleFaceShiftComplete = (result: any) => {
    setShowFaceShiftModal(false);
    // Dispatch AI creation complete event
    const event = new CustomEvent('aiCreationComplete', {
      detail: {
        type: 'face_shift',
        result,
        ritualId: currentRitualId
      }
    });
    document.dispatchEvent(event);
  };
  // Handle splash completion
  const handleSplashDone = () => {
    setSplashDone(true);
    try {
      localStorage.setItem('sinshift_splash_last_seen', String(Date.now()));
    } catch (e) {
      // Ignore storage errors
      console.log('Failed to save splash seen status', e);
    }
  };
  // Listen for AI creation events
  useEffect(() => {
    const handleAiCreationComplete = (event: any) => {
      if (event.detail) {
        const {
          type,
          result
        } = event.detail;
        // Update RewardHub with the new creation
        console.log(`AI Creation complete: ${type}`, result);
        // Show notification
        const notificationEvent = new CustomEvent('showNotification', {
          detail: {
            title: 'Creation Ready',
            message: `Your ${type === 'deep_shift' ? 'Deep Shift' : 'Face Shift'} creation is ready!`,
            type: 'success',
            action: {
              label: 'View in Reward Hub',
              onClick: () => {
                // Navigate to rewards section
                const navigateEvent = new CustomEvent('navigate', {
                  detail: {
                    section: 'rewards'
                  }
                });
                document.dispatchEvent(navigateEvent);
              }
            }
          }
        });
        document.dispatchEvent(notificationEvent);
        // Check if this completes any rituals
        const ritualEvent = new CustomEvent('checkRitualCompletion', {
          detail: {
            action: 'ai_creation',
            subtype: type
          }
        });
        document.dispatchEvent(ritualEvent);
      }
    };
    document.addEventListener('aiCreationComplete', handleAiCreationComplete);
    return () => {
      document.removeEventListener('aiCreationComplete', handleAiCreationComplete);
    };
  }, []);
  useEffect(() => {
    // Add custom animations to the stylesheet
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      @keyframes pulse-glow {
        0%, 100% { opacity: 0.7; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.05); }
      }
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        20% { transform: translateX(-10px) rotate(-2deg); }
        40% { transform: translateX(10px) rotate(2deg); }
        60% { transform: translateX(-10px) rotate(-1deg); }
        80% { transform: translateX(10px) rotate(1deg); }
      }
      @keyframes open-box {
        0% { transform: scale(1); }
        20% { transform: scale(1.1); }
        40% { transform: scale(0.9); }
        60% { transform: scale(1.05); }
        80% { transform: scale(0.95); }
        100% { transform: scale(1.2); opacity: 0; }
      }
      @keyframes float-spark {
        0% { transform: translateY(0) translateX(0); opacity: 1; }
        100% { transform: translateY(-20px) translateX(10px); opacity: 0; }
      }
      @keyframes spark-fly {
        0% { transform: translate(0, 0) scale(1); opacity: 1; }
        100% { transform: translate(var(--tw-translate-x, 0), var(--tw-translate-y, 0)) translateY(-50px) translateX(var(--random-x, 20px)) scale(0); opacity: 0; }
      }
      @keyframes spark-float {
        0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 1; }
        100% { transform: translateY(-15px) translateX(10px) rotate(360deg); opacity: 0; }
      }
      @keyframes bounce-small {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      @keyframes pop {
        0% { transform: scale(0); opacity: 0; }
        70% { transform: scale(1.2); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes float-particle {
        0% { transform: translate(0, 0); opacity: 1; }
        100% { transform: translate(var(--random-x, 10px), -20px); opacity: 0; }
      }
      .animate-shimmer {
        animation: shimmer 2s infinite;
      }
      .animate-pulse-glow {
        animation: pulse-glow 2s infinite;
      }
      .animate-shake {
        animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) infinite;
      }
      .animate-open-box {
        animation: open-box 1s forwards;
      }
      .animate-float-spark {
        animation: float-spark 3s ease-out forwards;
        animation-delay: var(--delay, 0s);
      }
      .animate-spark-fly {
        --random-x: ${Math.random() * 40 - 20}px;
        animation: spark-fly 1s ease-out forwards;
      }
      .animate-spark-float {
        animation: spark-float 2s ease-out forwards;
      }
      .animate-bounce-small {
        animation: bounce-small 1s infinite;
      }
      .animate-pop {
        animation: pop 0.3s forwards;
      }
      .animate-pulse-subtle {
        animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      }
      .animate-float-particle {
        animation: float-particle 10s ease-out infinite;
        animation-delay: var(--delay, 0s);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  useEffect(() => {
    const handleNavigate = (event: any) => {
      if (event.detail && event.detail.section) {
        setActiveSection(event.detail.section);
      }
    };
    document.addEventListener('navigate', handleNavigate);
    return () => {
      document.removeEventListener('navigate', handleNavigate);
    };
  }, []);
  // Add ShapeShift event handler to App.tsx
  useEffect(() => {
    const handleRitualSelected = (event: any) => {
      if (event.detail && event.detail.ritual) {
        const ritual = event.detail.ritual;
        const ritualId = ritual.id;
        const ritualName = (ritual.name || '').toLowerCase();
        const ritualDescription = (ritual.description || '').toLowerCase();
        const ritualText = ritualName + ' ' + ritualDescription;
        console.log('DEBUG: Ritual selected:', ritualText, ritualId);
        // Add ShapeShift ritual handling
        if (ritualText.includes('shapeshift') || ritualText.includes('shape') && ritualText.includes('shift') || ritualText.includes('persona') && ritualText.includes('transform')) {
          console.log('DEBUG: Direct match - ShapeShift');
          // Open ShapeShift modal
          const shapeShiftEvent = new CustomEvent('openShapeShiftModal', {
            detail: {
              ritualId: ritualId,
              mode: ritualText.includes('persona') ? 'persona' : ritualText.includes('scene') ? 'scene' : ritualText.includes('voice') ? 'voice' : ritualText.includes('mood') ? 'mood' : 'persona'
            }
          });
          document.dispatchEvent(shapeShiftEvent);
          return;
        }
        // DIRECT MATCHES - Check for exact ritual patterns first before any scoring
        // These are guaranteed to work for specific common rituals
        // Unleash Angel - Check this FIRST before any other checks
        if (ritualText.includes('unleash') && ritualText.includes('angel') || ritualText.includes('create') && ritualText.includes('angel') || ritualText.includes('personal') && ritualText.includes('angel')) {
          console.log('DEBUG: Direct match - Unleash Angel creation');
          setActiveSection('unleash');
          // Dispatch specific event to open Angel creator
          setTimeout(() => {
            const angelEvent = new CustomEvent('openAngelCreator', {
              detail: {
                ritualId: ritual.id,
                source: 'ritual'
              }
            });
            document.dispatchEvent(angelEvent);
          }, 100);
          return;
        }
        // Fantasy scenarios creation - Check this SECOND
        if (ritualText.includes('create') && ritualText.includes('fantasy') && ritualText.includes('scenario') || ritualText.includes('create') && ritualText.includes('fantasy') || ritualText.includes('fantasy scenario') && ritualText.includes('create') || ritualText.includes('create 3 fantasy scenario') || ritualText.includes('create 1 fantasy scenario') || ritualText.match(/create\s+\d+\s+fantasy\s+scenario/) ||
        // Match any "create X fantasy scenario" pattern
        ritualText.includes('unleash') && ritualText.includes('fantasy')) {
          console.log('DEBUG: Direct match - Fantasy scenario creation');
          setActiveSection('unleash');
          // Dispatch specific event to open scenario wizard
          setTimeout(() => {
            const scenarioEvent = new CustomEvent('navigateToScenarioWizard', {
              detail: {
                ritualId: ritual.id,
                ritual: {
                  id: ritualId,
                  name: ritualName,
                  description: ritualDescription
                }
              }
            });
            document.dispatchEvent(scenarioEvent);
          }, 100);
          return;
        }
        // Hot or Not voting
        if (ritualText.includes('cast') && ritualText.includes('vote') && ritualText.includes('hot or not') || ritualText.includes('hot or not') && ritualText.includes('vote') || ritualText.includes('hotornot') && ritualText.includes('vote') || ritualText.includes('rate') && ritualText.includes('selfie')) {
          console.log('DEBUG: Direct match - Hot or Not voting');
          setActiveSection('hot_or_not');
          // Mark ritual as complete after a delay
          setTimeout(() => {
            const completeEvent = new CustomEvent('ritualCompleted', {
              detail: {
                ritualId: ritualId,
                type: 'hot_or_not_vote'
              }
            });
            document.dispatchEvent(completeEvent);
          }, 500);
          return;
        }
        // Questionnaire completion
        if (ritualText.includes('complete') && ritualText.includes('questionnaire') || ritualText.includes('quick') && ritualText.includes('questionnaire') || ritualText.includes('fill') && ritualText.includes('questionnaire') || ritualText.includes('questionnaire')) {
          console.log('DEBUG: Direct match - Questionnaire completion');
          setActiveSection('chat');
          setActiveChat('chat');
          // Dispatch event to open questionnaire
          const questionnaireEvent = new CustomEvent('openQuestionnaire', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(questionnaireEvent);
          return;
        }
        // Create a ritual type classifier to determine the most likely ritual type
        const ritualTypeScores = {
          age_verification: 0,
          spin_wheel: 0,
          fantasy_scenario: 0,
          deep_shift: 0,
          face_shift: 0,
          hot_or_not: 0,
          questionnaire: 0
          // Add other ritual types here
        };
        // ==================== SCORING LOGIC ====================
        // Score Age Verification
        if (ritualText.includes('verify') || ritualText.includes('age verification') || ritualText.includes('identity verification') || ritualText.includes('age check')) {
          ritualTypeScores.age_verification += 5;
          if (ritualText.includes('age') || ritualText.includes('identity')) {
            ritualTypeScores.age_verification += 10;
          }
        }
        // Score Spin Wheel
        if (ritualText.includes('spin') || ritualText.includes('wheel') || ritualText.includes('fortune') || ritualText.includes('spinthewheelmodal') || ritualText.includes('enhanced spinwheel')) {
          ritualTypeScores.spin_wheel += 5;
          if (ritualText.includes('spin') && ritualText.includes('wheel') || ritualText.includes('wheel of fortune')) {
            ritualTypeScores.spin_wheel += 10;
          }
        }
        // Score Fantasy Scenario
        if (ritualText.includes('fantasy') || ritualText.includes('scenario') || ritualText.includes('scenariowizard') || ritualText.includes('create') && ritualText.includes('fantasy')) {
          ritualTypeScores.fantasy_scenario += 5;
          if (ritualText.includes('fantasy') && ritualText.includes('scenario') || ritualText.includes('create fantasy')) {
            ritualTypeScores.fantasy_scenario += 10;
          }
        }
        // Score Hot or Not
        if (ritualText.includes('hot or not') || ritualText.includes('hotornot') || ritualText.includes('rate') && ritualText.includes('selfie') || ritualText.includes('cast') && ritualText.includes('vote')) {
          ritualTypeScores.hot_or_not += 5;
          if (ritualText.includes('hot') && ritualText.includes('not') && ritualText.includes('vote') || ritualText.includes('cast') && ritualText.includes('vote') && ritualText.includes('hot')) {
            ritualTypeScores.hot_or_not += 15;
          }
        }
        // Score Questionnaire
        if (ritualText.includes('questionnaire') || ritualText.includes('complete') && ritualText.includes('survey') || ritualText.includes('fill') && ritualText.includes('form')) {
          ritualTypeScores.questionnaire += 5;
          if (ritualText.includes('quick questionnaire') || ritualText.includes('complete') && ritualText.includes('questionnaire')) {
            ritualTypeScores.questionnaire += 15;
          }
        }
        // Score DeepShift
        if (ritualText.includes('deepshift') || ritualText.includes('deep') && ritualText.includes('shift') || ritualText.includes('deep') && ritualText.includes('ai')) {
          ritualTypeScores.deep_shift += 5;
          // Reduce score if it also contains fantasy or scenario terms - INCREASED PENALTY
          if (ritualText.includes('fantasy') || ritualText.includes('scenario') || ritualText.includes('create') && ritualText.includes('fantasy')) {
            ritualTypeScores.deep_shift -= 50; // Increased from -10 to -50 to ensure it doesn't trigger
          }
          // Reduce score if it contains hot or not terms
          if (ritualText.includes('hot or not') || ritualText.includes('vote')) {
            ritualTypeScores.deep_shift -= 15;
          }
          // Reduce score if it contains questionnaire terms
          if (ritualText.includes('questionnaire') || ritualText.includes('survey')) {
            ritualTypeScores.deep_shift -= 15;
          }
        }
        // Score FaceShift
        if (ritualText.includes('faceshift') || ritualText.includes('face') && ritualText.includes('shift') || ritualText.includes('face') && ritualText.includes('ai')) {
          ritualTypeScores.face_shift += 5;
          // Reduce score if it also contains fantasy or scenario terms
          if (ritualText.includes('fantasy') || ritualText.includes('scenario')) {
            ritualTypeScores.face_shift -= 10;
          }
          // Reduce score if it contains hot or not terms
          if (ritualText.includes('hot or not') || ritualText.includes('vote')) {
            ritualTypeScores.face_shift -= 15;
          }
          // Reduce score if it contains questionnaire terms
          if (ritualText.includes('questionnaire') || ritualText.includes('survey')) {
            ritualTypeScores.face_shift -= 15;
          }
        }
        // Add common sense rules
        // If "create fantasy scenario" is in the text, it's definitely a fantasy scenario
        if (ritualText.includes('create') && ritualText.includes('fantasy') && ritualText.includes('scenario')) {
          ritualTypeScores.fantasy_scenario = 100;
          ritualTypeScores.deep_shift = 0;
          ritualTypeScores.face_shift = 0;
        }
        // If "verify age" or "age verification" is in the text, it's definitely age verification
        if (ritualText.includes('verify') && ritualText.includes('age') || ritualText.includes('age verification')) {
          ritualTypeScores.age_verification = 100;
          ritualTypeScores.deep_shift = 0;
          ritualTypeScores.face_shift = 0;
        }
        // If "spin wheel" or "wheel of fortune" is in the text, it's definitely a spin wheel
        if (ritualText.includes('spin') && ritualText.includes('wheel') || ritualText.includes('wheel of fortune') || ritualText.includes('spinthewheelmodal')) {
          ritualTypeScores.spin_wheel = 100;
          ritualTypeScores.deep_shift = 0;
          ritualTypeScores.face_shift = 0;
        }
        // If "hot or not" and "vote" are in the text, it's definitely hot or not
        if (ritualText.includes('hot') && ritualText.includes('not') && ritualText.includes('vote') || ritualText.includes('cast') && ritualText.includes('vote') && ritualText.includes('hot')) {
          ritualTypeScores.hot_or_not = 100;
          ritualTypeScores.deep_shift = 0;
          ritualTypeScores.face_shift = 0;
        }
        // If "questionnaire" is in the text, it's definitely a questionnaire
        if (ritualText.includes('questionnaire')) {
          ritualTypeScores.questionnaire = 100;
          ritualTypeScores.deep_shift = 0;
          ritualTypeScores.face_shift = 0;
        }
        // Find the ritual type with the highest score
        let highestScore = 0;
        let ritualType = null;
        for (const [type, score] of Object.entries(ritualTypeScores)) {
          if (score > highestScore) {
            highestScore = score;
            ritualType = type;
          }
        }
        console.log('DEBUG: Ritual type scores:', ritualTypeScores);
        console.log('DEBUG: Determined ritual type:', ritualType);
        // ==================== HANDLING LOGIC ====================
        // Handle based on the determined ritual type
        switch (ritualType) {
          case 'age_verification':
            console.log('DEBUG: Handling age verification ritual');
            // Clear the verification flag in localStorage to force showing the modal
            try {
              localStorage.removeItem('hasSeenVerification');
              setHasSeenVerification(false);
            } catch (e) {
              console.error('Failed to clear localStorage', e);
            }
            // Show the verification modal
            setShowVerification(true);
            // Mark the ritual as complete
            setTimeout(() => {
              const completeEvent = new CustomEvent('ritualCompleted', {
                detail: {
                  ritualId: ritualId,
                  type: 'age_verification'
                }
              });
              document.dispatchEvent(completeEvent);
            }, 500);
            return;
          case 'spin_wheel':
            console.log('DEBUG: Handling spin wheel ritual');
            // Set the active section to rewards where the wheel is located
            setActiveSection('rewards');
            // Dispatch wheel opening event
            const spinEvent = new CustomEvent('openSpinWheel', {
              detail: {
                ritualId: ritual.id,
                source: 'ritual_trigger'
              }
            });
            document.dispatchEvent(spinEvent);
            // Also dispatch a direct wheel spin event after a short delay
            setTimeout(() => {
              const spinWheelEvent = new CustomEvent('spinWheel', {
                detail: {
                  ritualId: ritual.id,
                  autoSpin: true
                }
              });
              document.dispatchEvent(spinWheelEvent);
              // Show a notification to guide the user
              const notificationEvent = new CustomEvent('showNotification', {
                detail: {
                  title: 'Wheel of Fortune',
                  message: 'Click the "Spin" button to spin the wheel!',
                  type: 'info',
                  duration: 5000
                }
              });
              document.dispatchEvent(notificationEvent);
            }, 1000);
            return;
          case 'fantasy_scenario':
            console.log('DEBUG: Handling fantasy scenario ritual');
            setActiveSection('unleash');
            // Dispatch specific events for premium or rating flows
            if (ritualText.includes('premium')) {
              const premiumEvent = new CustomEvent('startPremiumScenario', {
                detail: {
                  ritualId: ritual.id
                }
              });
              document.dispatchEvent(premiumEvent);
            } else if (ritualText.includes('rate')) {
              const ratingEvent = new CustomEvent('startScenarioRating', {
                detail: {
                  ritualId: ritual.id
                }
              });
              document.dispatchEvent(ratingEvent);
            } else {
              // Default scenario wizard event
              const scenarioEvent = new CustomEvent('navigateToScenarioWizard', {
                detail: {
                  ritualId: ritual.id,
                  ritual: {
                    id: ritualId,
                    name: ritualName,
                    description: ritualDescription
                  }
                }
              });
              document.dispatchEvent(scenarioEvent);
            }
            return;
          case 'hot_or_not':
            console.log('DEBUG: Handling Hot or Not ritual');
            setActiveSection('hot_or_not');
            // Dispatch event to focus on voting interface
            const voteEvent = new CustomEvent('focusHotOrNotVoting', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(voteEvent);
            // Mark ritual as complete after a delay
            setTimeout(() => {
              const completeEvent = new CustomEvent('ritualCompleted', {
                detail: {
                  ritualId: ritualId,
                  type: 'hot_or_not_vote'
                }
              });
              document.dispatchEvent(completeEvent);
            }, 500);
            return;
          case 'questionnaire':
            console.log('DEBUG: Handling Questionnaire ritual');
            setActiveSection('chat');
            setActiveChat('chat');
            // Dispatch event to open questionnaire
            const questionnaireEvent = new CustomEvent('openQuestionnaire', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(questionnaireEvent);
            return;
          case 'deep_shift':
            console.log('DEBUG: Handling DeepShift ritual');
            const deepShiftEvent = new CustomEvent('openDeepShiftModal', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(deepShiftEvent);
            return;
          case 'face_shift':
            console.log('DEBUG: Handling FaceShift ritual');
            const faceShiftEvent = new CustomEvent('openFaceShiftModal', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(faceShiftEvent);
            return;
        }
        // If we get here, either no ritual type was determined or it wasn't one of the main types
        // Fall back to the original ritual handling logic for other ritual types
        // Simple navigation rituals - handle these next
        // Open Reward Hub
        if (ritualText.includes('open') && (ritualText.includes('reward hub') || ritualText.includes('rewards')) || ritualText.includes('visit reward hub') || ritualText.includes('check rewards') || ritualText.includes('view rewards')) {
          setActiveSection('rewards');
          return;
        }
        // Open Bookmarks
        if (ritualText.includes('open') && ritualText.includes('bookmark') || ritualText.includes('view bookmarks') || ritualText.includes('check bookmarks')) {
          setActiveSection('bookmarks');
          return;
        }
        // Open Achievements
        if (ritualText.includes('open') && ritualText.includes('achievement') || ritualText.includes('view achievements') || ritualText.includes('check achievements') || ritualText.includes('view') && ritualText.includes('badges')) {
          setActiveSection('achievements');
          return;
        }
        // Open Social Polls
        if (ritualText.includes('open') && ritualText.includes('poll') || ritualText.includes('view polls') || ritualText.includes('check polls')) {
          setActiveSection('social_polls');
          return;
        }
        // Open Hot or Not
        if (ritualText.includes('open') && (ritualText.includes('hot or not') || ritualText.includes('hotornot')) || ritualText.includes('view hot or not') || ritualText.includes('check ratings')) {
          setActiveSection('hot_or_not');
          return;
        }
        // Open Angel Skins Shop
        if (ritualText.includes('open') && (ritualText.includes('skin') || ritualText.includes('shop')) || ritualText.includes('view angel skins') || ritualText.includes('check angel skins')) {
          setActiveSection('angel_skins_shop');
          return;
        }
        // Open Chat
        if (ritualText.includes('open') && ritualText.includes('chat') && !ritualText.includes('group') || ritualText.includes('start chat') || ritualText.includes('private chat')) {
          setActiveSection('chat');
          setActiveChat('chat');
          return;
        }
        // Open Group Chat
        if (ritualText.includes('open') && ritualText.includes('group chat') || ritualText.includes('start group chat')) {
          setActiveSection('group_chat');
          setActiveChat('group_chat');
          return;
        }
        // Open Fanclub
        if (ritualText.includes('open') && ritualText.includes('fanclub') || ritualText.includes('view fanclub') || ritualText.includes('check fanclub')) {
          setActiveSection('fanclub');
          return;
        }
        // Open Creator Dashboard
        if (ritualText.includes('open') && (ritualText.includes('creator') || ritualText.includes('dashboard')) || ritualText.includes('view creator dashboard') || ritualText.includes('check creator tools')) {
          setActiveSection('creator_dashboard');
          setActiveProfile('angel');
          return;
        }
        // Open Premium Content
        if (ritualText.includes('open') && ritualText.includes('premium') || ritualText.includes('view premium content') || ritualText.includes('check premium')) {
          setActiveSection('premium');
          return;
        }
        // Open VR/AR Section
        if (ritualText.includes('open') && (ritualText.includes('vr') || ritualText.includes('ar')) || ritualText.includes('view vr content') || ritualText.includes('check ar features')) {
          setActiveSection('vr_ar');
          return;
        }
        // Open Social Feed
        if (ritualText.includes('open') && (ritualText.includes('social') || ritualText.includes('feed')) || ritualText.includes('view social feed') || ritualText.includes('check feed')) {
          setActiveSection('social_feed');
          setActiveProfile('customer');
          return;
        }
        // Open Subscription/Membership
        if (ritualText.includes('open') && (ritualText.includes('subscription') || ritualText.includes('membership')) || ritualText.includes('view subscription') || ritualText.includes('check membership')) {
          setActiveSection('subscription');
          return;
        }
        // Open Token Wallet
        if (ritualText.includes('open') && (ritualText.includes('token') || ritualText.includes('wallet')) || ritualText.includes('view tokens') || ritualText.includes('check wallet')) {
          setActiveSection('token_wallet');
          return;
        }
        // Open Games Section
        if (ritualText.includes('open') && ritualText.includes('game') || ritualText.includes('view games') || ritualText.includes('play games')) {
          setActiveSection('games');
          return;
        }
        // Open Minigame
        if (ritualText.includes('open') && ritualText.includes('minigame') || ritualText.includes('play minigame')) {
          setActiveSection('minigame');
          return;
        }
        // Open Angel Match
        if (ritualText.includes('open') && ritualText.includes('angel match') || ritualText.includes('play angel match')) {
          setActiveSection('angel_match');
          return;
        }
        // Open Live Cam
        if (ritualText.includes('open') && (ritualText.includes('live') || ritualText.includes('cam')) || ritualText.includes('view live cam') || ritualText.includes('watch live')) {
          setActiveSection('live_cam');
          return;
        }
        // Open Unleash Feature
        if (ritualText.includes('open') && ritualText.includes('unleash') || ritualText.includes('view unleash') || ritualText.includes('check fantasy')) {
          setActiveSection('unleash');
          return;
        }
        // Open Profile Settings
        if (ritualText.includes('open') && (ritualText.includes('profile') || ritualText.includes('settings')) || ritualText.includes('view settings') || ritualText.includes('edit profile')) {
          setShowProfileSettings(true);
          return;
        }
        // Open Referral Modal
        if (ritualText.includes('open') && ritualText.includes('referral') || ritualText.includes('invite friends') || ritualText.includes('refer friends')) {
          const referralEvent = new CustomEvent('openReferralModal', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(referralEvent);
          return;
        }
        // Login streak flow
        if (ritualText.includes('login') && ritualText.includes('7 days')) {
          // No navigation needed, RitualTracker is always visible
          const trackerEvent = new CustomEvent('highlightLoginStreak', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(trackerEvent);
        }
        // Spin Wheel
        else if (ritualText.includes('spin') && ritualText.includes('wheel')) {
          // Dispatch event to open EnhancedSpinWheel
          const spinEvent = new CustomEvent('openSpinWheel', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(spinEvent);
        }
        // Playroom sessions
        else if (ritualText.includes('playroom sessions') || ritualText.includes('playroom') && !ritualText.includes('multi-angel')) {
          setActiveSection('group_chat');
          setActiveChat('group_chat');
        }
        // Gift to Angel
        else if (ritualText.includes('gift') && ritualText.includes('angel')) {
          setActiveSection('premium');
          // Dispatch event to open gift subflow
          const giftEvent = new CustomEvent('openGiftSubflow', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(giftEvent);
        }
        // Angel Skins
        else if (ritualText.includes('skins') && ritualText.includes('angel') || ritualText.includes('dress up') || ritualText.includes('equip skin') || ritualText.includes('angel skin') || ritualText.includes('equip') && ritualText.includes('playful angel skin')) {
          setActiveSection('angel_skins_shop');
          if (ritualText.includes('equip')) {
            const equipEvent = new CustomEvent('openEquipInterface', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(equipEvent);
          } else if (ritualText.includes('preview')) {
            const previewEvent = new CustomEvent('openSkinPreview', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(previewEvent);
          } else if (ritualText.includes('limited')) {
            const limitedEvent = new CustomEvent('showLimitedSkins', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(limitedEvent);
          }
        }
        // Hot or Not
        else if (ritualText.includes('hotornot') && ritualText.includes('vote') || ritualText.includes('selfies') && ritualText.includes('rate')) {
          setActiveSection('hot_or_not');
        }
        // Poll Generator
        else if (ritualText.includes('poll') && ritualText.includes('create') || ritualText.includes('custom poll') || ritualText.includes('kink') && ritualText.includes('poll') || ritualText.includes('pollgenerator')) {
          setActiveSection('social_polls');
          // Dispatch event to open PollGenerator
          const pollEvent = new CustomEvent('openPollGenerator', {
            detail: {
              ritualId: ritual.id,
              kinkMode: ritualText.includes('kink')
            }
          });
          document.dispatchEvent(pollEvent);
        }
        // Angel Match Game
        else if (ritualText.includes('angel match')) {
          setActiveSection('angel_match');
        }
        // Bookmarks
        else if (ritualText.includes('bookmark') || ritualText.includes('poll') && ritualText.includes('bookmark') || ritualText.includes('teaser photo') && ritualText.includes('bookmark') || ritualText.includes('fantasy') && ritualText.includes('bookmark') || ritualText.includes('pollcards') || ritualText.includes('bookmarks') && ritualText.includes('playful')) {
          setActiveSection('bookmarks');
          // Handle specific bookmark categories
          let category = null;
          if (ritualText.includes('fan stories')) {
            category = 'Fan Stories';
          } else if (ritualText.includes('polls') || ritualText.includes('pollcards')) {
            category = 'Polls';
          } else if (ritualText.includes('fantasy')) {
            category = 'Fantasy';
          } else if (ritualText.includes('playful')) {
            category = 'Playful';
          }
          if (category) {
            const categoryEvent = new CustomEvent('setBookmarkCategory', {
              detail: {
                category
              }
            });
            document.dispatchEvent(categoryEvent);
          }
        }
        // Social Life Section reactions
        else if (ritualText.includes('sparkles') && ritualText.includes('react') || ritualText.includes('sparkle reactions') || ritualText.includes('emoji reaction') || ritualText.includes('diamonds react') || ritualText.includes('gif') && ritualText.includes('feed') || ritualText.includes("friends' posts") || ritualText.includes('fun photo feed')) {
          setActiveSection('social_feed');
          setActiveProfile('customer');
          // Dispatch event for specific reaction type if needed
          if (ritualText.includes('sparkle') || ritualText.includes('sparkles')) {
            const reactEvent = new CustomEvent('showSparkleReactions', {});
            document.dispatchEvent(reactEvent);
          } else if (ritualText.includes('diamond')) {
            const reactEvent = new CustomEvent('showDiamondReactions', {});
            document.dispatchEvent(reactEvent);
          } else if (ritualText.includes('gif')) {
            const reactEvent = new CustomEvent('showGifPosting', {});
            document.dispatchEvent(reactEvent);
          }
        }
        // Social Life Section posting
        else if (ritualText.includes('post update')) {
          setActiveSection('social_feed');
          setActiveProfile('customer');
          const postEvent = new CustomEvent('showPostingInterface', {});
          document.dispatchEvent(postEvent);
        }
        // Lootbox Dashboard
        else if (ritualText.includes('lootbox') && !ritualText.includes('jackpot')) {
          setActiveSection('rewards');
          // Navigate to the appropriate section based on lootbox type
          if (ritualText.includes('weekly premium')) {
            const lootboxEvent = new CustomEvent('openPremiumLootbox', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(lootboxEvent);
          } else if (ritualText.includes('weekly passion')) {
            const lootboxEvent = new CustomEvent('openPassionLootbox', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(lootboxEvent);
          } else {
            const lootboxEvent = new CustomEvent('openLootboxDashboard', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(lootboxEvent);
          }
        }
        // Jackpot Lootbox Opening
        else if (ritualText.includes('jackpot lootbox')) {
          setActiveSection('rewards');
          const jackpotEvent = new CustomEvent('openJackpotLootbox', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(jackpotEvent);
        }
        // Chat Section
        else if (ritualText.includes('sticker') && ritualText.includes('chat') && !ritualText.includes('group chat') || ritualText.includes('voice note') || ritualText.includes('audio effect') || ritualText.includes('private fun chat') || ritualText.includes('nickname')) {
          setActiveSection('chat');
          setActiveChat('chat');
          // Dispatch specific events based on subflow
          if (ritualText.includes('sticker')) {
            const stickerEvent = new CustomEvent('openStickerInterface', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(stickerEvent);
          } else if (ritualText.includes('voice') || ritualText.includes('audio')) {
            const voiceEvent = new CustomEvent('openVoiceInterface', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(voiceEvent);
          } else if (ritualText.includes('nickname')) {
            const nicknameEvent = new CustomEvent('openNicknameInterface', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(nicknameEvent);
          }
        }
        // Multi-Angel Group Chat
        else if (ritualText.includes('multi-angel') && ritualText.includes('playroom') || ritualText.includes('playroom with 2 angels')) {
          setActiveSection('group_chat');
          setActiveChat('group_chat');
          const multiAngelEvent = new CustomEvent('startMultiAngelChat', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(multiAngelEvent);
        }
        // Questionnaire
        else if (ritualText.includes('questionnaire')) {
          setActiveSection('chat');
          setActiveChat('chat');
          const questionnaireEvent = new CustomEvent('openQuestionnaire', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(questionnaireEvent);
        }
        // VR/AR Section
        else if (ritualText.includes('ar filter') || ritualText.includes('vr environment') || ritualText.includes('teaser clip ar/vr') || ritualText.includes('ar emoji') || ritualText.includes('vr demo') || ritualText.includes('ar/vr playroom demo') || ritualText.includes('vr message')) {
          setActiveSection('vr_ar');
          // Dispatch specific events based on VR/AR subflow
          if (ritualText.includes('teaser')) {
            const teaserEvent = new CustomEvent('openVRARTeaser', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(teaserEvent);
          } else if (ritualText.includes('demo')) {
            const demoEvent = new CustomEvent('openVRARDemo', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(demoEvent);
          } else if (ritualText.includes('playroom')) {
            const playroomEvent = new CustomEvent('openVRARPlayroom', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(playroomEvent);
          } else if (ritualText.includes('message')) {
            const messageEvent = new CustomEvent('openVRARMessage', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(messageEvent);
          }
        }
        // Scenario Wizard / Fantasy - this is a fallback for any fantasy-related rituals that weren't caught above
        else if (ritualText.includes('fantasy') || ritualText.includes('scenariowizard') || ritualText.includes('fantasy scenarios') && ritualText.includes('rate')) {
          setActiveSection('unleash');
          // Dispatch specific events for premium or rating flows
          if (ritualText.includes('premium')) {
            const premiumEvent = new CustomEvent('startPremiumScenario', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(premiumEvent);
          } else if (ritualText.includes('rate')) {
            const ratingEvent = new CustomEvent('startScenarioRating', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(ratingEvent);
          }
        }
        // Dickpic Rating
        else if (ritualText.includes('friends opinion')) {
          setShowPhotoRatingFlow(true);
        }
        // Poll Feed
        else if ((ritualText.includes('trending poll') || ritualText.includes('pollfeed') && ritualText.includes('vote')) && !ritualText.includes('create')) {
          setActiveSection('social_polls');
          const pollFeedEvent = new CustomEvent('openPollFeed', {
            detail: {
              ritualId: ritual.id,
              trending: ritualText.includes('trending')
            }
          });
          document.dispatchEvent(pollFeedEvent);
        }
        // Reward Hub / Ritual Rewards
        else if (ritualText.includes('claim reward') || ritualText.includes('rewardhub') && ritualText.includes('claim') || ritualText.includes('ritualrewards')) {
          setActiveSection('rewards');
        }
        // Mini Game
        else if (ritualText.includes('minigame')) {
          setActiveSection('minigame');
        }
        // Angel Social Feed
        else if (ritualText.includes('comment') && ritualText.includes('angel') || ritualText.includes('angel feeds')) {
          // If there's a selected angel, navigate to their feed
          if (selectedAngelId) {
            setActiveSection('twitter_profile');
            setShowAngelFeed(true);
          } else {
            // Otherwise go to discovery feed
            setActiveSection('discovery_feed');
          }
        }
        // Group Chat Section
        else if (ritualText.includes('comment') && ritualText.includes('playroom') || ritualText.includes('save') && ritualText.includes('playroom') || ritualText.includes('group chat') && ritualText.includes('sticker') || ritualText.includes('playroom') && ritualText.includes('30 minutes') || ritualText.includes('premium') && ritualText.includes('playroom') && ritualText.includes('event')) {
          setActiveSection('group_chat');
          setActiveChat('group_chat');
          // Dispatch specific events based on subflow
          if (ritualText.includes('save')) {
            const saveEvent = new CustomEvent('openPlayroomSave', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(saveEvent);
          } else if (ritualText.includes('sticker')) {
            const stickerEvent = new CustomEvent('openGroupStickerInterface', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(stickerEvent);
          } else if (ritualText.includes('30 minutes')) {
            const timedEvent = new CustomEvent('startTimedPlayroom', {
              detail: {
                ritualId: ritual.id,
                duration: 30
              }
            });
            document.dispatchEvent(timedEvent);
          } else if (ritualText.includes('premium') && ritualText.includes('event')) {
            const premiumEvent = new CustomEvent('startPremiumPlayroomEvent', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(premiumEvent);
          }
        }
        // Live Cam / Tease Preview
        else if (ritualText.includes('live angel teaser') || ritualText.includes('teasepreviewmodal') || ritualText.includes('livecam')) {
          setActiveSection('live_cam');
          if (ritualText.includes('teasepreviewmodal')) {
            const teaseEvent = new CustomEvent('openTeasePreview', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(teaseEvent);
          }
        }
        // Premium Content
        else if (ritualText.includes('premium content') || ritualText.includes('sticker') && ritualText.includes('pack') || ritualText.includes('roses') && !ritualText.includes('gift') || ritualText.includes('digital') && ritualText.includes('hug') || ritualText.includes('sparkle') && ritualText.includes('gift')) {
          setActiveSection('premium');
          // Dispatch event for specific premium content type
          if (ritualText.includes('sticker pack')) {
            const stickerEvent = new CustomEvent('openStickerPackUpsell', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(stickerEvent);
          }
        }
        // Upsell Flows
        else if (ritualText.includes('ai image') || ritualText.includes('premium video') || ritualText.includes('supplement gift') || ritualText.includes('toysupsell')) {
          setActiveSection('premium');
          // Dispatch specific upsell events
          if (ritualText.includes('ai image')) {
            const imageEvent = new CustomEvent('openImageGenerationUpsell', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(imageEvent);
          } else if (ritualText.includes('premium video')) {
            const videoEvent = new CustomEvent('openVideoGenerationUpsell', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(videoEvent);
          } else if (ritualText.includes('supplement')) {
            const supplementEvent = new CustomEvent('openSupplementUpsell', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(supplementEvent);
          } else if (ritualText.includes('toysupsell')) {
            const toysEvent = new CustomEvent('openToysUpsell', {
              detail: {
                ritualId: ritual.id,
                demoMode: ritualText.includes('demo')
              }
            });
            document.dispatchEvent(toysEvent);
          }
        }
        // Spin The Wheel Modal
        else if (ritualText.includes('spinthewheelmodal') || ritualText.includes('enhanced spinwheel')) {
          setActiveSection('rewards');
          const spinEvent = new CustomEvent('openSpinWheel', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(spinEvent);
        }
        // Fanclub Section
        else if (ritualText.includes('fanclub event') || ritualText.includes('fanclub') && ritualText.includes('react') || ritualText.includes('fanclub') && ritualText.includes('gift') || ritualText.includes('fanclub') && ritualText.includes('comment') || ritualText.includes('fanclub') && ritualText.includes('posts') || ritualText.includes('multi-angel') && ritualText.includes('fanclub') && ritualText.includes('chat')) {
          setActiveSection('fanclub');
          // Dispatch specific events based on fanclub subflow
          if (ritualText.includes('react')) {
            const reactEvent = new CustomEvent('showFanclubReactions', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(reactEvent);
          } else if (ritualText.includes('gift')) {
            const giftEvent = new CustomEvent('showFanclubGifts', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(giftEvent);
          } else if (ritualText.includes('multi-angel') && ritualText.includes('chat')) {
            const chatEvent = new CustomEvent('startMultiAngelFanclubChat', {
              detail: {
                ritualId: ritual.id
              }
            });
            document.dispatchEvent(chatEvent);
          }
        }
        // Games Section
        else if (ritualText.includes('gamessection')) {
          setActiveSection('games');
        }
        // Social Polls Section
        else if (ritualText.includes('socialpolls')) {
          setActiveSection('social_polls');
        }
        // Photo Rating Flow
        else if (ritualText.includes('photoratingflow')) {
          setShowPhotoRatingFlow(true);
        }
        // Voice AI Layer
        else if (ritualText.includes('personalized voice')) {
          setActiveSection('chat');
          const voiceEvent = new CustomEvent('activateVoiceAI', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(voiceEvent);
        }
        // Sparkle Badge
        else if (ritualText.includes('sparkle badge')) {
          setActiveSection('rewards');
          const badgeEvent = new CustomEvent('showSparkleBadge', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(badgeEvent);
        }
        // Referral system
        else if (ritualText.includes('refer') || ritualText.includes('referral')) {
          const referralEvent = new CustomEvent('openReferralModal', {
            detail: {
              ritualId: ritual.id
            }
          });
          document.dispatchEvent(referralEvent);
        }
        // Achievements section
        else if (ritualText.includes('achievement') || ritualText.includes('badge')) {
          setActiveSection('achievements');
        }
        // Creator tools/dashboard
        else if (ritualText.includes('creator') || ritualText.includes('content creation')) {
          setActiveSection('creator_dashboard');
          setActiveProfile('angel');
        }
        // Token wallet/store
        else if (ritualText.includes('token') || ritualText.includes('gems') || ritualText.includes('coins') || ritualText.includes('purchase')) {
          setActiveSection('token_wallet');
        }
        // Membership/subscription
        else if (ritualText.includes('subscription') || ritualText.includes('membership')) {
          setActiveSection('subscription');
        }
      }
    };
    document.addEventListener('ritualSelected', handleRitualSelected);
    return () => {
      document.removeEventListener('ritualSelected', handleRitualSelected);
    };
  }, [setActiveSection, setActiveChat, setShowAngelFeed, selectedAngelId, setShowPhotoRatingFlow, setActiveProfile, setShowProfileSettings, setShowVerification, setHasSeenVerification]);
  // Add handler for ShapeShift completion
  useEffect(() => {
    const handleShapeShiftComplete = (event: any) => {
      if (event.detail) {
        const {
          result,
          ritualId
        } = event.detail;
        // Show notification
        const notificationEvent = new CustomEvent('showNotification', {
          detail: {
            title: 'ShapeShift Complete',
            message: `Your ${result.type} shift has been applied!`,
            type: 'success',
            action: {
              label: 'View in Playroom',
              onClick: () => {
                // Navigate to group chat section
                const navigateEvent = new CustomEvent('navigate', {
                  detail: {
                    section: 'group_chat'
                  }
                });
                document.dispatchEvent(navigateEvent);
              }
            }
          }
        });
        document.dispatchEvent(notificationEvent);
        // Check if this completes any rituals
        if (ritualId) {
          const ritualEvent = new CustomEvent('ritualCompleted', {
            detail: {
              ritualId,
              type: 'shape_shift'
            }
          });
          document.dispatchEvent(ritualEvent);
        }
      }
    };
    document.addEventListener('shapeShiftComplete', handleShapeShiftComplete);
    return () => {
      document.removeEventListener('shapeShiftComplete', handleShapeShiftComplete);
    };
  }, []);
  // Add a direct wheel spin handler
  useEffect(() => {
    const handleSpinWheelAction = (event: any) => {
      // This will handle any direct "spin wheel" actions from UI buttons
      console.log('DEBUG: Spin wheel action triggered');
      const spinWheelEvent = new CustomEvent('spinWheel', {
        detail: {
          autoSpin: true,
          source: 'direct_action'
        }
      });
      document.dispatchEvent(spinWheelEvent);
      // Also show the wheel UI if it's not already visible
      const openWheelEvent = new CustomEvent('openSpinWheel', {
        detail: {
          source: 'direct_action'
        }
      });
      document.dispatchEvent(openWheelEvent);
      // Navigate to rewards section where the wheel is located
      setActiveSection('rewards');
    };
    document.addEventListener('spinWheelAction', handleSpinWheelAction);
    return () => {
      document.removeEventListener('spinWheelAction', handleSpinWheelAction);
    };
  }, [setActiveSection]);
  // Enhance direct event listeners for ritual "go" buttons
  useEffect(() => {
    const handleRitualGoButtonClick = (event: any) => {
      if (event.detail && event.detail.ritualType && event.detail.ritualId) {
        const ritualType = event.detail.ritualType.toLowerCase();
        const ritualId = event.detail.ritualId;
        const primaryCategory = event.detail.primaryCategory;
        console.log('DEBUG: Ritual go button clicked:', ritualType, ritualId, primaryCategory);
        // DIRECT MATCHES - Handle specific ritual types directly
        // Unleash Angel - Check this FIRST
        if (primaryCategory === 'unleash' && ritualType.includes('unleash') && ritualType.includes('angel')) {
          console.log('DEBUG: Direct match - Unleash Angel (Go button handler)');
          setActiveSection('unleash');
          // Open angel creator
          setTimeout(() => {
            const angelEvent = new CustomEvent('openAngelCreator', {
              detail: {
                ritualId: ritualId,
                source: 'ritual'
              }
            });
            document.dispatchEvent(angelEvent);
          }, 100);
          return;
        }
        // Fantasy Scenario - Check this SECOND
        if (primaryCategory === 'fantasy' || ritualType.includes('create') && ritualType.includes('fantasy') || ritualType.includes('fantasy scenario') || ritualType.match(/create\s+\d+\s+fantasy\s+scenario/) || ritualType.includes('unleash') && ritualType.includes('fantasy')) {
          console.log('DEBUG: Direct match - Fantasy Scenario (Go button handler)');
          setActiveSection('unleash');
          // Open scenario wizard
          setTimeout(() => {
            const wizardEvent = new CustomEvent('navigateToScenarioWizard', {
              detail: {
                ritualId: ritualId,
                ritual: {
                  id: ritualId,
                  name: ritualType
                }
              }
            });
            document.dispatchEvent(wizardEvent);
          }, 100);
          return;
        }
        // Hot or Not
        if (primaryCategory === 'hot_or_not' || ritualType.includes('hot or not') && ritualType.includes('vote') || ritualType.includes('cast') && ritualType.includes('vote')) {
          console.log('DEBUG: Direct match - Hot or Not (Go button handler)');
          setActiveSection('hot_or_not');
          // Focus on voting interface
          setTimeout(() => {
            const focusEvent = new CustomEvent('focusHotOrNotVoting', {
              detail: {
                ritualId: ritualId
              }
            });
            document.dispatchEvent(focusEvent);
          }, 300);
          return;
        }
        // Questionnaire
        if (primaryCategory === 'questionnaire' || ritualType.includes('questionnaire')) {
          console.log('DEBUG: Direct match - Questionnaire (Go button handler)');
          setActiveSection('chat');
          setActiveChat('chat');
          // Open questionnaire
          setTimeout(() => {
            const questionnaireEvent = new CustomEvent('openQuestionnaire', {
              detail: {
                ritualId: ritualId
              }
            });
            document.dispatchEvent(questionnaireEvent);
          }, 300);
          return;
        }
        // Determine ritual category using a scoring system similar to the main handler
        const scores = {
          wheel: 0,
          age: 0,
          fantasy: 0,
          deep_shift: 0,
          face_shift: 0,
          hot_or_not: 0,
          questionnaire: 0
        };
        // Score each category
        if (ritualType.includes('spin') || ritualType.includes('wheel') || ritualType.includes('fortune')) {
          scores.wheel += 10;
        }
        if (ritualType.includes('verify') || ritualType.includes('age') || ritualType.includes('identity')) {
          scores.age += 10;
        }
        if (ritualType.includes('fantasy') || ritualType.includes('scenario') || ritualType.includes('create')) {
          scores.fantasy += 10;
        }
        if (ritualType.includes('hot or not') || ritualType.includes('vote') || ritualType.includes('rate')) {
          scores.hot_or_not += 10;
        }
        if (ritualType.includes('questionnaire') || ritualType.includes('survey')) {
          scores.questionnaire += 10;
        }
        if (ritualType.includes('deep') || ritualType.includes('shift') || ritualType.includes('deep') && ritualType.includes('ai')) {
          scores.deep_shift += 5;
        }
        if (ritualType.includes('face') || ritualType.includes('face') && ritualType.includes('shift')) {
          scores.face_shift += 5;
        }
        // Common sense overrides
        if (ritualType.includes('spin') && ritualType.includes('wheel') || ritualType.includes('wheel of fortune')) {
          scores.wheel = 100;
          scores.deep_shift = 0;
          scores.face_shift = 0;
        }
        if (ritualType.includes('verify') && ritualType.includes('age') || ritualType.includes('age verification')) {
          scores.age = 100;
          scores.deep_shift = 0;
          scores.face_shift = 0;
        }
        if (ritualType.includes('fantasy') && ritualType.includes('scenario') || ritualType.includes('create') && ritualType.includes('fantasy')) {
          scores.fantasy = 100;
          scores.deep_shift = 0;
          scores.face_shift = 0;
        }
        if (ritualType.includes('hot') && ritualType.includes('not') || ritualType.includes('cast') && ritualType.includes('vote')) {
          scores.hot_or_not = 100;
          scores.deep_shift = 0;
          scores.face_shift = 0;
        }
        if (ritualType.includes('questionnaire')) {
          scores.questionnaire = 100;
          scores.deep_shift = 0;
          scores.face_shift = 0;
        }
        console.log('DEBUG: Go button ritual scores:', scores);
        // Find highest scoring category
        let highestScore = 0;
        let category = null;
        for (const [cat, score] of Object.entries(scores)) {
          if (score > highestScore) {
            highestScore = score;
            category = cat;
          }
        }
        console.log('DEBUG: Determined go button ritual category:', category);
        // Handle based on determined category
        switch (category) {
          case 'wheel':
            console.log('DEBUG: Handling wheel ritual go button');
            setActiveSection('rewards');
            // Trigger both the UI opening and the actual spin
            const openEvent = new CustomEvent('openSpinWheel', {
              detail: {
                ritualId: ritualId,
                source: 'go_button'
              }
            });
            document.dispatchEvent(openEvent);
            // Force the wheel to spin after a short delay
            setTimeout(() => {
              const spinEvent = new CustomEvent('spinWheel', {
                detail: {
                  ritualId: ritualId,
                  autoSpin: true,
                  source: 'go_button'
                }
              });
              document.dispatchEvent(spinEvent);
            }, 800);
            break;
          case 'age':
            console.log('DEBUG: Handling age verification ritual go button');
            // Clear localStorage and show verification modal
            try {
              localStorage.removeItem('hasSeenVerification');
              setHasSeenVerification(false);
            } catch (e) {
              console.error('Failed to clear localStorage', e);
            }
            setShowVerification(true);
            break;
          case 'fantasy':
            console.log('DEBUG: Handling fantasy scenario ritual go button');
            setActiveSection('unleash');
            // Dispatch event to scenario wizard
            const scenarioEvent = new CustomEvent('navigateToScenarioWizard', {
              detail: {
                ritualId: ritualId,
                ritual: {
                  id: ritualId,
                  name: ritualType
                }
              }
            });
            document.dispatchEvent(scenarioEvent);
            break;
          case 'hot_or_not':
            console.log('DEBUG: Handling Hot or Not ritual go button');
            setActiveSection('hot_or_not');
            // Focus on voting interface
            setTimeout(() => {
              const focusEvent = new CustomEvent('focusHotOrNotVoting', {
                detail: {
                  ritualId: ritualId
                }
              });
              document.dispatchEvent(focusEvent);
            }, 300);
            break;
          case 'questionnaire':
            console.log('DEBUG: Handling Questionnaire ritual go button');
            setActiveSection('chat');
            setActiveChat('chat');
            // Open questionnaire
            setTimeout(() => {
              const questionnaireEvent = new CustomEvent('openQuestionnaire', {
                detail: {
                  ritualId: ritualId
                }
              });
              document.dispatchEvent(questionnaireEvent);
            }, 300);
            break;
          case 'deep_shift':
            console.log('DEBUG: Handling DeepShift ritual go button');
            const deepShiftEvent = new CustomEvent('openDeepShiftModal', {
              detail: {
                ritualId: ritualId
              }
            });
            document.dispatchEvent(deepShiftEvent);
            break;
          case 'face_shift':
            console.log('DEBUG: Handling FaceShift ritual go button');
            const faceShiftEvent = new CustomEvent('openFaceShiftModal', {
              detail: {
                ritualId: ritualId
              }
            });
            document.dispatchEvent(faceShiftEvent);
            break;
          default:
            console.log('DEBUG: No specific handler for this ritual go button, using fallback');
            // Fallback: dispatch the original ritual selected event
            const ritualEvent = new CustomEvent('ritualSelected', {
              detail: {
                ritual: {
                  id: ritualId,
                  name: ritualType,
                  description: ''
                }
              }
            });
            document.dispatchEvent(ritualEvent);
        }
      }
    };
    document.addEventListener('ritualGoButtonClick', handleRitualGoButtonClick);
    return () => {
      document.removeEventListener('ritualGoButtonClick', handleRitualGoButtonClick);
    };
  }, [setActiveSection, setShowVerification, setHasSeenVerification, setActiveChat]);
  useEffect(() => {
    const handleNavigateToScenarioWizard = (event: any) => {
      if (event.detail && event.detail.ritual) {
        // Navigate to unleash section for ScenarioWizard
        setActiveSection('unleash');
      }
    };
    document.addEventListener('navigateToScenarioWizard', handleNavigateToScenarioWizard);
    return () => {
      document.removeEventListener('navigateToScenarioWizard', handleNavigateToScenarioWizard);
    };
  }, []);
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowProfileSettings(true);
    };
    document.addEventListener('openProfileSettings', handleOpenSettings);
    return () => {
      document.removeEventListener('openProfileSettings', handleOpenSettings);
    };
  }, []);
  const handleCloseWelcomeVideo = () => {
    setShowWelcomeVideo(false);
    localStorage.setItem('hasSeenWelcomeVideo', 'true');
  };
  const handleOpenSettings = () => {
    setShowProfileSettings(true);
  };
  const angels = [
    // ... existing angels data ...
  ];
  useEffect(() => {
    localStorage.setItem('createdAngels', JSON.stringify(createdAngels));
  }, [createdAngels]);
  const handleNavigation = (section: string) => {
    setActiveSection(section);
    if (section !== 'twitter_profile') {
      setShowAngelFeed(false);
      setSelectedAngelId(null);
    }
  };
  const handleAngelSelect = (angelId: string) => {
    setSelectedAngelId(angelId);
    setShowAngelFeed(true);
    setActiveSection('twitter_profile');
  };
  useEffect(() => {
    const handleSelectAngel = (event: any) => {
      const angelId = event.detail.angelId;
      if (angelId) {
        setSelectedAngelId(angelId);
        setShowAngelFeed(true);
      }
    };
    document.addEventListener('selectAngel', handleSelectAngel);
    return () => {
      document.removeEventListener('selectAngel', handleSelectAngel);
    };
  }, []);
  useEffect(() => {
    const handleNavigateToCustomerProfile = () => {
      setActiveSection('social_feed');
      setActiveProfile('customer');
    };
    document.addEventListener('navigateToCustomerProfile', handleNavigateToCustomerProfile);
    return () => {
      document.removeEventListener('navigateToCustomerProfile', handleNavigateToCustomerProfile);
    };
  }, []);
  const handleCloseVerification = () => {
    setShowVerification(false);
    setHasSeenVerification(true);
    localStorage.setItem('hasSeenVerification', 'true');
  };
  const handleAngelCreated = (angelData: AngelData) => {
    setCreatedAngels([...createdAngels, angelData]);
  };
  const handleActionSelect = (action: string) => {
    switch (action) {
      // ... existing action handlers ...
      default:
        console.log(`Action not implemented: ${action}`);
    }
  };
  const getCurrentAngelContext = () => {
    if (activeSection === 'twitter_profile' && showAngelFeed && selectedAngelId) {
      const angel = angels.find(a => a.id === selectedAngelId);
      return angel ? angel.name : undefined;
    }
    return undefined;
  };
  const renderContent = () => {
    switch (activeSection) {
      case 'angel_skins_shop':
        return <AngelSkinsShop data-id="element-0" />;
      case 'profile':
        return <ProfilePanel data-id="element-1" />;
      case 'social_feed':
        return <div className="w-full" data-id="element-2">
            <div className="flex justify-center mb-4" data-id="element-3">
              <ProfileToggle activeProfile={activeProfile} onToggle={setActiveProfile} data-id="element-4" />
            </div>
            {activeProfile === 'customer' ? <SocialLifeSection data-id="element-5" /> : <CreatorStudioDashboard data-id="element-6" />}
          </div>;
      case 'creator_dashboard':
        return <div className="w-full" data-id="element-7">
            <div className="flex justify-center mb-4" data-id="element-8">
              <ProfileToggle activeProfile="angel" onToggle={profile => {
              setActiveProfile(profile);
              if (profile === 'customer') {
                setActiveSection('social_feed');
              }
            }} data-id="element-9" />
            </div>
            <CreatorStudioDashboard data-id="element-10" />
          </div>;
      case 'discovery_feed':
        return <FilteredDiscoveryFeed onAngelSelect={handleAngelSelect} data-id="element-11" />;
      case 'all_angels':
        return <AllAngelsGrid angels={angels} onAngelSelect={handleAngelSelect} data-id="element-12" />;
      case 'fanclub':
        return <FanclubSection data-id="element-13" />;
      case 'minigame':
        return <MiniGame data-id="element-14" />;
      case 'dashboard':
        return <Dashboard data-id="element-15" />;
      case 'token_wallet':
        return <TokenWallet data-id="element-16" />;
      case 'celebrity_ai':
        return <CelebrityAI data-id="element-17" />;
      case 'live_cam':
        return <LiveCam data-id="element-18" />;
      case 'chat':
        return <ChatSection setShowAngelFeed={setShowAngelFeed} setSelectedAngelId={setSelectedAngelId} data-id="element-19" />;
      case 'group_chat':
        return <GroupChatSection setShowAngelFeed={setShowAngelFeed} setSelectedAngelId={setSelectedAngelId} onNavigate={handleNavigation} data-id="element-20" />;
      case 'games':
        return <GamesSection data-id="element-21" />;
      case 'angel_match':
        return <AngelMatchGame data-id="element-22" />;
      case 'stars':
        return <StarsSection data-id="element-23" />;
      case 'stats':
        return <StatsSection data-id="element-24" />;
      case 'social_life':
        return <SocialLifeSection data-id="element-25" />;
      case 'interactive_toy':
        return <InteractiveToySync data-id="element-26" />;
      case 'yoga':
        return <YogaPost data-id="element-27" />;
      case 'vr_ar':
        return <VRARSection data-id="element-28" />;
      case 'advanced_token':
        return <AdvancedTokenSystem data-id="element-29" />;
      case 'creator_marketplace':
        return <CreatorMarketplace data-id="element-30" />;
      case 'achievements':
        return <AchievementsSection data-id="element-31" />;
      case 'rewards':
        return <RewardHub data-id="element-32" />;
      case 'achievements':
        return <RitualAchievements data-id="element-33" />;
      case 'premium':
        return <PremiumContent onBack={() => handleNavigation('social_feed')} data-id="element-34" />;
      case 'subscription':
        return <MembershipSection onBack={() => handleNavigation('social_feed')} data-id="element-35" />;
      case 'twitter_profile':
        return showAngelFeed ? selectedAngelId === '11' ? <LanaAngelFeed onBack={() => setShowAngelFeed(false)} data-id="element-36" /> : selectedAngelId === '1' ? <SophiaAngelFeed onBack={() => setShowAngelFeed(false)} data-id="element-37" /> : selectedAngelId === '2' ? <BonnyBlueAngelFeed onBack={() => setShowAngelFeed(false)} data-id="element-38" /> : selectedAngelId === '3' ? <KimHollandAngelFeed onBack={() => setShowAngelFeed(false)} data-id="element-39" /> : selectedAngelId === '4' ? <BellaBlueAngelFeed onBack={() => setShowAngelFeed(false)} data-id="element-40" /> : selectedAngelId === '5' ? <HarperAngelFeed onBack={() => setShowAngelFeed(false)} data-id="element-41" /> : selectedAngelId === '6' ? <ZoeyAngelFeed onBack={() => setShowAngelFeed(false)} data-id="element-42" /> : selectedAngelId === '7' ? <MiaKhalifaAngelFeed onBack={() => setShowAngelFeed(false)} data-id="element-43" /> : <TwitterStyleProfile data-id="element-44" /> : <TwitterStyleProfile data-id="element-45" />;
      case 'unleash':
        return <UnleashFeature onBack={() => handleNavigation('discovery_feed')} onAngelCreated={handleAngelCreated} data-id="element-46" />;
      case 'relationship_tracking':
        return <RelationshipTracking data-id="element-47" />;
      case 'social_polls':
        return <SocialPollsSection data-id="element-48" />;
      case 'hot_or_not':
        return <HotOrNotSection onBack={() => handleNavigation('discovery_feed')} data-id="element-49" />;
      case 'bookmarks':
        return <BookmarksSection data-id="element-50" />;
      case 'favorites':
        return <FavoritesSection data-id="element-51" />;
      case 'rituals':
        return <RitualBoard data-id="element-52" />;
      case 'reward_hub':
        return <RewardHub data-id="element-53" />;
      case 'engagement_rituals':
        return <EngagementRituals data-id="element-54" />;
      default:
        return <ProfilePanel data-id="element-55" />;
    }
  };
  useEffect(() => {
    const handleOpenReferralModal = (event: any) => {
      if (event.detail && event.detail.ritualId) {
        setReferralRitualId(event.detail.ritualId);
      }
      setShowReferralModal(true);
    };
    document.addEventListener('openReferralModal', handleOpenReferralModal);
    return () => {
      document.removeEventListener('openReferralModal', handleOpenReferralModal);
    };
  }, []);
  // Add this function to manually test the age verification
  const handleTestAgeVerification = () => {
    // Clear localStorage and show the verification modal
    try {
      localStorage.removeItem('hasSeenVerification');
      setHasSeenVerification(false);
      setShowVerification(true);
    } catch (e) {
      console.error('Failed to test age verification', e);
    }
  };
  return <>
      {/* Splash Screen with Suspense fallback */}
      {showSplash && !splashDone && <Suspense fallback={<div className="fixed inset-0 z-[60] bg-[#0a0a12] flex items-center justify-center" data-id="element-57">
              <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" data-id="element-58"></div>
            </div>} data-id="element-56">
          <SplashScreen imageUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/ctUKNy7zNs9njQaTKAMctq/welcome_to_sinshift_astonmartin_b.webp" autoHideMs={6000} onDone={handleSplashDone} data-id="element-59" />
        </Suspense>}
      {/* Main application */}
      <ThemeProvider data-id="element-60">
        <TranslationProvider data-id="element-61">
          <QuestionnaireProvider data-id="element-62">
            <RitualProvider data-id="element-63">
              <div className="flex flex-col w-full min-h-screen bg-white dark:bg-black text-of-text-primary dark:text-white app-container" data-id="element-64">
                <TopHeader tokenCount={240} notificationCount={15} onNotificationsClick={() => setShowNotifications(!showNotifications)} onNavigate={handleNavigation} activeSection={activeSection} onOpenSettings={handleOpenSettings} data-id="element-65" />
                {/* Add this for testing - you can remove it later */}
                {process.env.NODE_ENV === 'development' && <div className="fixed bottom-4 right-4 z-50" data-id="element-66">
                    <button onClick={handleTestAgeVerification} className="px-3 py-1 bg-red-500 text-white text-xs rounded-md shadow-md" data-id="element-67">
                      Test Age Verification
                    </button>
                    <button onClick={() => setShowGuidedHelp(true)} className="ml-2 px-3 py-1 bg-blue-500 text-white text-xs rounded-md shadow-md" data-id="element-68">
                      Show Guided Tour
                    </button>
                  </div>}
                <LeftNavBar activeSection={activeSection} onNavigate={handleNavigation} data-id="element-69" />
                <main className="container mx-auto flex flex-col py-6 ml-16 md:ml-20 mt-14 max-w-full overflow-x-hidden" data-id="element-70">
                  <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8" data-id="element-71">
                    {activeSection === 'discovery_feed' && <div className="mb-2 flex flex-col md:flex-row items-center justify-center gap-2" data-id="element-72">
                        <button onClick={() => handleNavigation('all_angels')} className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:shadow-md hover:shadow-pink-500/20 transition-all animate-pulse-subtle" data-id="element-73">
                          <span className="text-lg" data-id="element-74">👼</span>
                          <span data-id="element-75">View All Angels</span>
                          <span className="ml-1 bg-white/30 px-1.5 py-0.5 rounded-full text-xs" data-id="element-76">
                            New!
                          </span>
                        </button>
                        <button onClick={() => handleNavigation('creator_marketplace')} className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:shadow-md hover:shadow-blue-500/20 transition-all" data-id="element-77">
                          <Sparkles size={16} data-id="element-78" />
                          <span data-id="element-79">Angel Marketplace</span>
                          <span className="ml-1 bg-white/30 px-1.5 py-0.5 rounded-full text-xs" data-id="element-80">
                            Hot!
                          </span>
                        </button>
                      </div>}
                    <div className="relative w-full bg-white dark:bg-black rounded-xl shadow-sm" data-id="element-81">
                      <div className="p-1 sm:p-3 md:p-4 lg:p-6 w-full overflow-hidden" data-id="element-82">
                        {activeSection === 'all_angels' && <div className="mb-4" data-id="element-83">
                            <button onClick={() => handleNavigation('discovery_feed')} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 backdrop-filter backdrop-blur-md border border-gray-100 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" data-id="element-84">
                              <ChevronLeft size={16} data-id="element-85" />
                              <span data-id="element-86">Back to Uncover</span>
                            </button>
                          </div>}
                        {activeSection === 'angel_skins_shop' && <div className="mb-4" data-id="element-87">
                            <button onClick={() => handleNavigation('discovery_feed')} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 backdrop-filter backdrop-blur-md border border-gray-100 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" data-id="element-88">
                              <ChevronLeft size={16} data-id="element-89" />
                              <span data-id="element-90">Back to Discovery</span>
                            </button>
                          </div>}
                        {activeSection === 'angel_match' && <div className="mb-4" data-id="element-91">
                            <button onClick={() => handleNavigation('games')} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 backdrop-filter backdrop-blur-md border border-gray-100 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" data-id="element-92">
                              <ChevronLeft size={16} data-id="element-93" />
                              <span data-id="element-94">Back to Games</span>
                            </button>
                            <div className="mt-2 flex items-center justify-between" data-id="element-95">
                              <div className="flex items-center gap-2" data-id="element-96">
                                <span className="px-2 py-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs rounded-full" data-id="element-97">
                                  HOT
                                </span>
                                <h2 className="text-lg font-bold" data-id="element-98">
                                  Angel Match Quest
                                </h2>
                              </div>
                              <button className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm hover:shadow-md hover:shadow-pink-500/20 transition-all" data-id="element-99">
                                <Diamond size={16} data-id="element-100" />
                                <span data-id="element-101">Unlock Grand Prize</span>
                              </button>
                            </div>
                          </div>}
                        {activeSection === 'rewards' && <div className="mb-4 flex items-center justify-between" data-id="element-102">
                            <button onClick={() => handleNavigation('social_feed')} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 backdrop-filter backdrop-blur-md border border-gray-100 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" data-id="element-103">
                              <ChevronLeft size={16} data-id="element-104" />
                              <span data-id="element-105">Back to Feed</span>
                            </button>
                            <button onClick={() => handleNavigation('rituals')} className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-lg hover:shadow-md hover:shadow-purple-400/20 transition-all" data-id="element-106">
                              <Trophy size={16} data-id="element-107" />
                              <span data-id="element-108">Rituals Board</span>
                            </button>
                          </div>}
                        {activeSection === 'creator_marketplace' && <div className="mb-4 flex flex-wrap items-center justify-between gap-2" data-id="element-109">
                            <button onClick={() => handleNavigation('discovery_feed')} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 backdrop-filter backdrop-blur-md border border-gray-100 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" data-id="element-110">
                              <ChevronLeft size={16} data-id="element-111" />
                              <span data-id="element-112">Back to Discovery</span>
                            </button>
                            <div className="flex flex-wrap gap-2" data-id="element-113">
                              <button onClick={() => {
                            // Navigate to angel skins section in marketplace
                            const event = new CustomEvent('changeMarketplaceCategory', {
                              detail: {
                                category: 'angel_skins'
                              }
                            });
                            document.dispatchEvent(event);
                          }} className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm hover:shadow-md hover:shadow-purple-500/20 transition-all" data-id="element-114">
                                <Sparkles size={16} data-id="element-115" />
                                <span data-id="element-116">Angel Skins</span>
                              </button>
                              <button onClick={() => {
                            // Navigate to premium videos section in marketplace
                            const event = new CustomEvent('changeMarketplaceCategory', {
                              detail: {
                                category: 'premium_videos'
                              }
                            });
                            document.dispatchEvent(event);
                          }} className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-lg text-sm hover:shadow-md hover:shadow-pink-500/20 transition-all" data-id="element-117">
                                <Play size={16} data-id="element-118" />
                                <span data-id="element-119">Premium Videos</span>
                              </button>
                              <button onClick={() => {
                            // Navigate to picture sets section in marketplace
                            const event = new CustomEvent('changeMarketplaceCategory', {
                              detail: {
                                category: 'picture_sets'
                              }
                            });
                            document.dispatchEvent(event);
                          }} className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg text-sm hover:shadow-md hover:shadow-blue-500/20 transition-all" data-id="element-120">
                                <Camera size={16} data-id="element-121" />
                                <span data-id="element-122">Picture Sets</span>
                              </button>
                            </div>
                          </div>}
                        {activeSection === 'premium' && <div className="mb-4 flex items-center justify-between" data-id="element-123">
                            <button onClick={() => handleNavigation('social_feed')} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 backdrop-filter backdrop-blur-md border border-gray-100 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" data-id="element-124">
                              <ChevronLeft size={16} data-id="element-125" />
                              <span data-id="element-126">Back to Feed</span>
                            </button>
                            <button onClick={() => handleNavigation('fanclub')} className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-400 to-cyan-500 text-white rounded-lg hover:shadow-md hover:shadow-blue-400/20 transition-all" data-id="element-127">
                              <Star size={16} data-id="element-128" />
                              <span data-id="element-129">Celebrity AI Angels</span>
                              <span className="ml-1 bg-white/30 px-1.5 py-0.5 rounded-full text-xs" data-id="element-130">
                                New!
                              </span>
                            </button>
                          </div>}
                        {activeSection === 'hot_or_not' && <div className="mb-4" data-id="element-131">
                            <button onClick={() => handleNavigation('discovery_feed')} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 backdrop-filter backdrop-blur-md border border-gray-100 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" data-id="element-132">
                              <ChevronLeft size={16} data-id="element-133" />
                              <span data-id="element-134">Back to Discovery</span>
                            </button>
                          </div>}
                        {activeSection === 'creator_dashboard' && <div className="mb-4 flex items-center justify-end" data-id="element-135">
                            <button onClick={() => {
                          // Dispatch event to change tab in CreatorStudioDashboard to AI image generator
                          const event = new CustomEvent('changeCreatorTab', {
                            detail: {
                              tab: 'ai-image-generator'
                            }
                          });
                          document.dispatchEvent(event);
                        }} className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-400 to-cyan-500 text-white rounded-lg hover:shadow-md hover:shadow-blue-400/20 transition-all animate-pulse-subtle" data-id="element-136">
                              <Camera size={16} data-id="element-137" />
                              <span data-id="element-138">AI Image Studio</span>
                              <span className="ml-1 bg-white/30 px-1.5 py-0.5 rounded-full text-xs flex items-center" data-id="element-139">
                                <Sparkles size={10} className="mr-0.5" data-id="element-140" />
                                New!
                              </span>
                            </button>
                          </div>}
                        {renderContent()}
                      </div>
                    </div>
                  </div>
                </main>
                <FloatingActionButton onActionSelect={handleActionSelect} angelContext={getCurrentAngelContext()} data-id="element-141" />
                <RitualTracker data-id="element-142" />
                <AlertSystem data-id="element-143" />
                <ProfileSettings isOpen={showProfileSettings} onClose={() => setShowProfileSettings(false)} userType={activeProfile} data-id="element-144" />
                {showWelcomeVideo && <WelcomeVideoModal onClose={handleCloseWelcomeVideo} data-id="element-145" />}
                {showVerification && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-146">
                    <div className="relative w-full max-w-lg max-h-[90vh] overflow-auto animate-in zoom-in-90 duration-300 bg-white dark:bg-black rounded-lg shadow-lg" data-id="element-147">
                      <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-of-gray dark:bg-gray-900" onClick={handleCloseVerification} data-id="element-148">
                        <X size={18} className="text-of-text-secondary dark:text-gray-400" data-id="element-149" />
                      </button>
                      <AgeVerification onClose={handleCloseVerification} data-id="element-150" />
                    </div>
                  </div>}
                {showPhotoRatingFlow && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-151">
                    <PhotoRatingFlow onClose={() => setShowPhotoRatingFlow(false)} data-id="element-152" />
                  </div>}
                {/* DeepShift Modal */}
                {showDeepShiftModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-153">
                    <DeepShiftModal onClose={() => setShowDeepShiftModal(false)} onComplete={handleDeepShiftComplete} ritualId={currentRitualId} data-id="element-154" />
                  </div>}
                {/* FaceShift Modal */}
                {showFaceShiftModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-155">
                    <FaceShiftModal onClose={() => setShowFaceShiftModal(false)} onComplete={handleFaceShiftComplete} ritualId={currentRitualId} data-id="element-156" />
                  </div>}
                {showReferralModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-157">
                    <ReferralModal onClose={() => setShowReferralModal(false)} ritualId={referralRitualId} data-id="element-158" />
                  </div>}
                {/* GuidedHelpOverlay */}
                <GuidedHelpOverlay isOpen={showGuidedHelp} onClose={() => setShowGuidedHelp(false)} onNavigate={handleNavigation} data-id="element-159" />
              </div>
            </RitualProvider>
          </QuestionnaireProvider>
        </TranslationProvider>
      </ThemeProvider>
    </>;
}